import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  GET_EVALUATION_REPORTS_BY_UUIDS,
  GET_POLICIES_BY_IDS,
  governanceApiRef,
} from '@agilelab/plugin-wb-governance';
import {
  EvaluationReportByUUIDQuery,
  PoliciesByIdsQuery,
  Timing,
} from '@agilelab/plugin-wb-governance-common';
import { useApolloClient } from '@apollo/client';
import { useApi, identityApiRef } from '@mainstage/core-plugin-api';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { useAsync } from 'react-use';
import { AsyncState } from 'react-use/lib/useAsyncFn';
import { useFlagAndScoreContext } from '../../FlagAndScoreContext';

export interface PolicyItem {
  result?: DagStatus;
  id: string;
  name: string;
  version?: number;
  date: string;
  timing: Timing | undefined;
  description: string | null;
}

interface PoliciesTabProps {
  resourceInfoExternalId: string;
  children?: ReactNode;
}

interface PoliciesTabType {
  activeEntityExternalId: string | undefined;
  setActiveEntityExternalId: Dispatch<SetStateAction<string | undefined>>;
  policyViolationsState: AsyncState<PolicyItem[] | undefined>;
}

const PoliciesTabContext = createContext<PoliciesTabType>(
  {} as PoliciesTabType,
);

export const PoliciesTabContextProvider: FC<PoliciesTabProps> = ({
  children,
  resourceInfoExternalId,
}) => {
  const identityApi = useApi(identityApiRef);
  const governanceApi = useApi(governanceApiRef);
  const apolloClient = useApolloClient();
  const { environment } = useFlagAndScoreContext();
  const [activeEntityExternalId, setActiveEntityExternalId] = useState<
    string | undefined
  >(resourceInfoExternalId);

  const policyViolationsState = useAsync(async () => {
    if (!activeEntityExternalId || !environment) {
      return undefined;
    }

    const credentials = await identityApi.getCredentials();

    const response = await governanceApi.getPolicyViolations(
      {
        resource: activeEntityExternalId,
        environment: environment,
      },
      credentials,
    );

    const policyViolations = response.violations;

    if (!policyViolations || policyViolations.length === 0) {
      return undefined;
    }

    const uniquePolicyIds = [...new Set(policyViolations.map(v => v.policyId))];
    const uniqueReportIds = [...new Set(policyViolations.map(v => v.reportId))];

    const [policiesResponse, reportsResponse] = await Promise.all([
      apolloClient.query<PoliciesByIdsQuery>({
        query: GET_POLICIES_BY_IDS,
        variables: { policyIds: uniquePolicyIds },
        fetchPolicy: 'cache-first',
      }),
      apolloClient.query<EvaluationReportByUUIDQuery>({
        query: GET_EVALUATION_REPORTS_BY_UUIDS,
        variables: { uuids: uniqueReportIds },
        fetchPolicy: 'cache-first',
      }),
    ]);

    const policiesMap = new Map(
      policiesResponse.data.cgp_governance_entity.map(policy => [
        policy.governance_entity_id,
        policy,
      ]),
    );

    const reportsMap = new Map(
      reportsResponse.data.cgp_evaluation_report.map(
        report => [report.uuid, report] as const,
      ),
    );

    return policyViolations
      .map(violation => {
        const policy = policiesMap.get(violation.policyId);
        const report = reportsMap.get(violation.reportId);
        if (!policy || !report) {
          return undefined;
        }

        return {
          id: violation.evaluationResultId,
          name: policy.name,
          version: policy.version,
          date: report.update_time,
          description: policy.description,
          result: violation.outcome.toUpperCase() as DagStatus,
          timing: policy.timing as Timing,
        };
      })
      .filter(Boolean) as PolicyItem[];
  }, [
    identityApi,
    governanceApi,
    activeEntityExternalId,
    environment,
    apolloClient,
  ]);

  return (
    <PoliciesTabContext.Provider
      value={{
        activeEntityExternalId,
        setActiveEntityExternalId,
        policyViolationsState,
      }}
    >
      {children}
    </PoliciesTabContext.Provider>
  );
};

export const usePolicyTab = () => useContext(PoliciesTabContext);
